import React from "react";
import { motion } from "framer-motion";
import Breadcrumb from "../components/Breadcrumb";
import methodologyData from "../content/methodologyData.json";

const Methodology = () => {
  const { intro, methodology, keyFactors } = methodologyData;
  const centerX = 300;
  const centerY = 300;
  const centerRadius = 50;
  const orbitRadius = 150;
  const nodeRadius = 40;
  const numNodes = methodology.nodes.length;

  // Calculate node positions
  const nodes = Array.from({ length: numNodes }, (_, i) => {
    const angle = (i * 2 * Math.PI) / numNodes - Math.PI / 2;
    const x = centerX + orbitRadius * Math.cos(angle);
    const y = centerY + orbitRadius * Math.sin(angle);
    return { ...methodology.nodes[i], x, y };
  });

  return (
    <>
      <Breadcrumb
        title="Methodology"
        items={[{ name: "Home", link: "/" }, { name: "Methodology" }]}
      />
      {/* Intro Section */}
      <div className="bg-ofWhite py-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-5 items-center">
          <div>
            <h2 className="text-2xl lg:text-ml-6xl font-normal text-red leading-tight mb-5">
              {intro.title}
            </h2>
            {intro.paragraphs.map((p, i) => (
              <p key={i} className="mb-2 text-gray-700 text-justify">
                {p}
              </p>
            ))}
          </div>

          {/* Animated Diagram */}
          <div className="flex justify-center relative">
            <motion.svg viewBox="100 100 500 400" className="w-full h-full">
              {nodes.map((node, i) => (
                <motion.line
                  key={`line-${i}`}
                  x1={centerX}
                  y1={centerY}
                  x2={node.x}
                  y2={node.y}
                  stroke="#1f2937"
                  strokeWidth="1"
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: 0.6,
                    delay: 1 + i * 0.4,
                    ease: "easeOut",
                  }}
                />
              ))}

              {nodes.map((node, i) => (
                <motion.g key={`node-${i}`}>
                  <motion.circle
                    cx={node.x}
                    cy={node.y}
                    r={nodeRadius + 10}
                    fill={node.color}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 1 + i * 0.4 }}
                  />
                  <motion.text
                    x={node.x}
                    y={node.y}
                    textAnchor="middle"
                    dy="0.3em"
                    fontSize="12"
                    fontWeight="600"
                    fill="white"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 1.4 + i * 0.4 }}
                  >
                    {node.name}
                  </motion.text>
                </motion.g>
              ))}

              {/* Center Node */}
              <motion.circle
                cx={centerX}
                cy={centerY}
                r={centerRadius}
                fill={methodology.center.color}
                stroke="#1f2937"
                strokeWidth="1"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6 }}
              />
              <motion.text
                x={centerX}
                y={centerY}
                textAnchor="middle"
                dy="0.3em"
                fontSize="14"
                fontWeight="600"
                fill="#fff"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                {methodology.center.text}
              </motion.text>
            </motion.svg>
          </div>
        </div>
      </div>

      {/* Key Factors Section */}
      <div className="bg-white3 py-10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl lg:text-ml-6xl font-normal text-red mb-5">
            {keyFactors.title}
          </h2>
          <p className="text-gray-700 mb-5">{keyFactors.description}</p>

          <div className="grid grid-cols-1 sm:grid-cols-1 gap-5">
            {keyFactors.factors.map((factor, i) => (
              <div key={i} className="ml-10">
                <h5 className="font-semibold text-red mb-1">{factor.title}</h5>
                <p className="text-gray-600 text-sm text-justify">
                  {factor.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Methodology;
